import { Close as CloseIcon } from "@mui/icons-material";
import { Autocomplete, Box, IconButton, InputAdornment, TextField, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";

import Countries from "../../../config/Countries.json";
import useContainerDimensions from "../../../utils/useContainerDimensions";

const useStyles = makeStyles((theme) => ({
  sharedStyle: {
    "& label.Mui-focused": {
      color: `${theme.palette.primary.main} !important`,
    },
    "& .MuiOutlinedInput-input": {
      "&:-webkit-autofill": {
        transition: "background-color 5000s ease-in-out 0s",
        "-webkit-text-fill-color": `${theme.palette.primary.main} !important`,
      },
      "&:-webkit-autofill:hover": {
        transition: "background-color 5000s ease-in-out 0s",
        "-webkit-text-fill-color": `${theme.palette.primary.main} !important`,
      },
      "&:-webkit-autofill:focus": {
        transition: "background-color 5000s ease-in-out 0s",
        "-webkit-text-fill-color": `${theme.palette.primary.main} !important`,
      },
      "&:-webkit-autofill:active": {
        transition: "background-color 5000s ease-in-out 0s",
        "-webkit-text-fill-color": `${theme.palette.primary.main} !important`,
      },
    },
    "& .Mui-focused .MuiOutlinedInput-input": {},
    "& .MuiInput-underline:after": {
      borderBottomColor: theme.palette.primary.main,
    },
    "@media (max-width:576px)": {
      "& .MuiOutlinedInput-input": {
        fontSize: "13px",
      },
      "& label": {
        fontSize: "13px",
      },
    },
  },
  textField: {
    "& .MuiInputAdornment-root": {
      backgroundColor: "transparent !important",
    },
    "& label": {
      color: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.common.primary),
    },
    "& .MuiInputBase-root": {
      paddingLeft: "4px",
    },
    "& .MuiOutlinedInput-input": {
      backgroundColor: "transparent",
      color: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.common.black),
    },
    "& .MuiOutlinedInput-root:not(.Mui-error)": {
      "& fieldset": {
        borderColor: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.neutral[200]),
      },
      "&:hover fieldset": {
        borderColor: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.neutral[200]),
      },
      "&.Mui-focused fieldset": {
        borderColor: `${theme.palette.primary.main} !important`,
      },
    },
  },
  autocompleteField: {
    "& .MuiFormControl-root": {
      minWidth: "90px !important",
      maxWidth: "100px !important",
    },
    "& label": {
      color: theme.palette.common.black,
    },
    "& .MuiOutlinedInput-input": {
      backgroundColor: "transparent",
      color: theme.palette.common.black,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.neutral[200]),
        border: "none !important",
      },
      "&:hover fieldset": {
        borderColor: (props) => (props.background === "dark" ? theme.palette.common.white : theme.palette.neutral[200]),
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
}));

const countries = Countries.map((country) => ({
  ...country,
  label: `${country.code} ${country.iso} ${country.name}`.toLowerCase(),
}));

export default function FormPhoneField(props) {
  const classes = useStyles(props);

  const {
    label,
    style = {},
    onChange,
    onBlur,
    name,
    defaultValue = {},
    size = "medium",
    variant = "outlined",
    control,
    setValue,
    className,
    disabled,
    ...rest
  } = props;

  const inputRef = useRef(null);
  const matches = useMediaQuery("(max-width:600px)");
  const { width: inputWidth } = useContainerDimensions(inputRef);
  const [countryCode, setCountryCode] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [phone, setPhone] = useState({
    number: defaultValue.number,
    iso: defaultValue.iso,
    code: defaultValue.code,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [finishedCountryCodeInput, setFinishedCountryCodeInput] = useState(false);

  useEffect(() => {
    const { number, iso, code } = defaultValue;
    setPhone({ number, iso, code });
  }, [defaultValue.number, defaultValue.iso, defaultValue.code]);

  const handleChange = (data) => {
    setValue(name, data);

    if (onChange) {
      onChange(data);
    }
  };
  const handlePhoneNumberChange = (value) => {
    setPhone({ ...phone, number: value });
  };

  const handleClearText = (field) => {
    if (onChange) onChange("");
    field.onChange({ ...phone, number: "" });
    setPhone({ ...phone, number: "" });
  };

  const handleBlur = (data) => {
    setValue(name, data);

    if (onBlur) {
      onBlur();
    }
  };

  useEffect(() => {
    setCountryCode(countries.find((country) => country.iso === phone.iso && country.code === phone.code));
  }, [phone.iso, phone.code]);

  useEffect(() => {
    setFinishedCountryCodeInput(true);
  }, [phone.code]);

  const getPhoneNumber = (number) => {
    const newValue = { number: number || phone.number || "" };

    if (countryCode) {
      newValue.code = countryCode.code;
      newValue.iso = countryCode.iso;
    }

    return newValue;
  };

  function displayIntlDisclaimerAndError(field, fieldState) {
    const { isDirty, isTouched, invalid, error } = fieldState;
    const { value = { number: "", code: "", iso: "" } } = field;
    const { iso: country } = value;

    if ((isDirty || isTouched) && invalid && error?.number?.message) {
      return error.number.message;
    }

    if (country !== "US" && error?.number === undefined) {
      return "SMS notifications will only be delivered to US phone numbers";
    }

    return null;
  }

  return (
    <div style={style}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const { invalid, isDirty, isTouched } = fieldState;
          const helperText = displayIntlDisclaimerAndError(field, fieldState);

          return (
            <TextField
              variant={variant}
              name={name}
              label={label}
              ref={inputRef}
              disabled={disabled}
              fullWidth
              onChange={(e) => {
                handlePhoneNumberChange(e.target.value);
                handleChange(getPhoneNumber(e.target.value));
              }}
              onBlur={() => {
                field.onChange(getPhoneNumber());
                field.onBlur();
                handleBlur(getPhoneNumber());
              }}
              value={phone.number}
              size={size}
              classes={{ root: `${classes.sharedStyle} ${classes.textField}` }}
              error={(isDirty || isTouched) && invalid}
              FormHelperTextProps={{
                "data-testid": "form-phone-field-helper-text",
              }}
              helperText={helperText}
              inputProps={{
                "data-testid": "form-phone-field",
              }}
              InputProps={{
                fullWidth: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Autocomplete
                      id="country-select-demo"
                      disableClearable
                      options={countries}
                      key=""
                      label={label}
                      autoHighlight
                      variant={variant}
                      value={countryCode}
                      disabled={disabled}
                      onOpen={() => setIsOpen(true)}
                      onClose={() => setIsOpen(false)}
                      sx={
                        style.color
                          ? {
                              ".MuiAutocomplete-popupIndicator": {
                                color: style.color,
                              },
                            }
                          : {}
                      }
                      filterOptions={(list, { inputValue: searchText }) => {
                        if (!searchText || (searchText && finishedCountryCodeInput)) {
                          return list;
                        }
                        const newList = list.filter((item) => item.label.indexOf(searchText.toLowerCase()) > -1);
                        return [...newList];
                      }}
                      onChange={(event, newValue) => {
                        setCountryCode(newValue);
                        handleChange(getPhoneNumber());
                      }}
                      inputValue={inputValue}
                      onInputChange={(event, newInputValue) => {
                        if (event) {
                          setFinishedCountryCodeInput(false);
                        }
                        setInputValue(newInputValue);
                      }}
                      onBlur={() => {
                        setFinishedCountryCodeInput(true);
                        handleBlur(getPhoneNumber());
                        field.onBlur();
                      }}
                      classes={{ root: `${classes.autocompleteField} ${classes.sharedStyle}` }}
                      getOptionLabel={(option) => (isOpen ? option.label : option.code)}
                      renderOption={(renderProps, option) => {
                        const { key, ...restOfRenderProps } = renderProps;

                        return (
                          <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            key={key}
                            {...restOfRenderProps}
                          >
                            <img
                              loading="lazy"
                              width="20"
                              src={`/icons/flags/${option.flag}`}
                              srcSet={`/icons/flags/${option.flag}`}
                              alt=""
                            />
                            {!matches ? option.name : ""} ({option.iso}) {option.code}
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          id="country-code"
                          {...params}
                          variant={variant}
                          disabled={disabled}
                          inputProps={{
                            fullWidth: true,
                            ...params.inputProps,
                            autoComplete: "new-password",
                            "data-testid": "form-country-code-field",
                          }}
                          onBlur={() => setFinishedCountryCodeInput(true)}
                        />
                      )}
                      componentsProps={{
                        paper: {
                          sx: {
                            width: inputWidth,
                            marginLeft: "-4px",
                          },
                        },
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton
                    onClick={() => handleClearText(field)}
                    sx={{ visibility: field.value?.number ? "visible" : "hidden" }}
                  >
                    <CloseIcon htmlColor="#757575" />
                  </IconButton>
                ),
              }}
              className={className}
              {...rest}
            />
          );
        }}
      />
    </div>
  );
}
